import { Children, isValidElement, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

// Replacement for the two owlCarousel instances configured in custom.js:
//   .home-slider        -> items 1, loop, nav, dots, smartSpeed 1000
//   .testimonial-slider -> items 1, loop, nav, no dots, animateOut 'fadeOut'
//
// style.css and responsive.css style .owl-prev/.owl-next/.owl-dots directly, so
// this renders the same DOM shape Owl produced (owl-stage-outer > owl-stage >
// owl-item, plus owl-nav and owl-dots) and the stylesheets apply untouched.
// Autoplay was false in both configs, so there is no timer here.

type CarouselProps = {
  /** e.g. "home-slider" — combined with owl-theme owl-carousel. */
  className: string;
  children: ReactNode;
  nav?: boolean;
  dots?: boolean;
  effect?: "slide" | "fade";
  loop?: boolean;
  /** smartSpeed equivalent, in ms. */
  speed?: number;
};

export default function Carousel({
  className,
  children,
  nav = true,
  dots = false,
  effect = "slide",
  loop = true,
  speed = 1000,
}: CarouselProps) {
  // Only real elements become slides — stray whitespace between the converted
  // markup's slide divs must not produce empty ones.
  const items = Children.toArray(children).filter(isValidElement);
  const count = items.length;
  const [index, setIndex] = useState(0);
  const visited = useRef(new Set([0]));
  visited.current.add(index);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      if (loop) setIndex(((next % count) + count) % count);
      else setIndex(Math.min(Math.max(next, 0), count - 1));
    },
    [count, loop]
  );

  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);

  // Keyboard support the jQuery version never had.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  useEffect(() => {
    if (index > count - 1) setIndex(0);
  }, [count, index]);

  if (count === 0) return null;

  const isFade = effect === "fade";

  return (
    <div
      className={`${className} owl-carousel owl-theme owl-loaded owl-drag`}
      role="region"
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
        touchStartX.current = null;
      }}
    >
      <div className="owl-stage-outer">
        <div
          className="owl-stage"
          style={
            isFade
              ? { position: "relative" }
              : {
                  display: "flex",
                  width: `${count * 100}%`,
                  transform: `translate3d(-${(index * 100) / count}%, 0, 0)`,
                  transition: `transform ${speed}ms ease`,
                }
          }
        >
          {items.map((child, i) => (
            <div
              key={i}
              className={`owl-item${i === index ? " active" : ""}${visited.current.has(i) ? "" : " slide-unvisited"}`}
              aria-hidden={i === index ? undefined : true}
              // React 18 emits this native boolean attribute using an empty string.
              inert={i === index ? undefined : ("" as unknown as boolean)}
              style={
                isFade
                  ? {
                      position: i === 0 ? "relative" : "absolute",
                      inset: i === 0 ? undefined : 0,
                      width: "100%",
                      opacity: i === index ? 1 : 0,
                      transition: `opacity ${speed}ms ease`,
                      pointerEvents: i === index ? "auto" : "none",
                    }
                  : { width: `${100 / count}%`, flex: "0 0 auto" }
              }
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {nav && count > 1 && (
        <div className="owl-nav">
          <button type="button" className="owl-prev" aria-label="Previous slide" onClick={prev}>
            <i className="icofont-simple-left" />
          </button>
          <button type="button" className="owl-next" aria-label="Next slide" onClick={next}>
            <i className="icofont-simple-right" />
          </button>
        </div>
      )}

      {dots && count > 1 && (
        <div className="owl-dots">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`owl-dot${i === index ? " active" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => go(i)}
            >
              <span />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
